import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { CorrectionNeededBehaviorComponent } from './correction-needed-behavior/correction-needed-behavior.component';
import { CorrectionNeededAssessmentComponent } from './correction-needed-assessment/correction-needed-assessment.component';
import { CorrectionNeededReviewEmailComponent } from './correction-needed-review-email/correction-needed-review-email.component';
import { CorrectionNeededStrategyComponent } from './correction-needed-strategy/correction-needed-strategy.component';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import {
  BehaviorInputData,
  CorrectionNeededInterface,
  FidelityCheckModel,
  FidelityReviewResult,
  FormEventEmit,
  sendEmailPayload,
} from '../../fidelity.interface';
import { FidelityCheckService } from '../../fidelity.service';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { DrawerService } from '../../../../core/services/drawer.service';
import { TabIndex, ReviewStatus } from '../../fidelity.enums';
import { CommonModule } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../../core/models/error.models';
import { CorrectionNeededDataComponent } from './correction-needed-data/correction-needed-data.component';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';

@Component({
  selector: 'app-fidelity-check-correction-needed',
  standalone: true,
  imports: [
    NzFormModule,
    NzTabsModule,
    NzButtonModule,
    CorrectionNeededBehaviorComponent,
    CorrectionNeededAssessmentComponent,
    CorrectionNeededReviewEmailComponent,
    CorrectionNeededStrategyComponent,
    CorrectionNeededDataComponent,
    CommonModule,
    NzSpinModule,
  ],
  templateUrl: './fidelity-check-correction-needed.component.html',
  styleUrl: './fidelity-check-correction-needed.component.scss',
})
export class FidelityCheckCorrectionNeededComponent implements OnDestroy {
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  drawerParams: CorrectionNeededInterface | null = null;
  fidelityData: FidelityReviewResult | null = null;
  selectedTabIndex = 0;
  selectedTab = 'Behavior';
  behvaiorInputData = {} as BehaviorInputData;
  fidelityPaylod!: FidelityCheckModel;
  errorMessage!: string;
  emailPayload!: sendEmailPayload;
  isFormValid = false;
  tabIndex = TabIndex;
  reviewStatus = ReviewStatus;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;
  constructor(
    private drawerService: DrawerService,
    private loaderService: LoaderService,
    private fidelityService: FidelityCheckService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private reportLabelsService: ReportLabelsService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .subscribe(value => {
        this.fidelityCheckLabel = value;
      });
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerParams();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<CorrectionNeededInterface>(
        DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.getfidelityByBehvaiorIdandType();
        }
      });
  }

  async onSelectTab(index: number) {
    this.selectedTabIndex = index;
    this.selectedTab = this.getVisibleTabs()[index] || '';

    if (this.selectedTab !== 'Review Email') {
      this.setBehvaiorData();
    }
  }

  private getVisibleTabs(): string[] {
    const visibleTabs: string[] = [];

    const tabConditions = [
      { key: 'Behavior', data: this.fidelityData?.behavior },
      { key: 'Assesment', data: this.fidelityData?.assesment },
      { key: 'Strategy', data: this.fidelityData?.stretagy },
      { key: 'Data', data: this.fidelityData?.data },
    ];

    tabConditions.forEach(tab => {
      if (
        tab.data &&
        (tab.data.reviewStatusId === this.reviewStatus['Changes Requested'] ||
          tab.data.reviewStatusId === this.reviewStatus['Changes Received'])
      ) {
        visibleTabs.push(tab.key);
      }
    });

    visibleTabs.push('Review Email'); // Review Email is always last
    return visibleTabs;
  }

  isTabVisible(tabName: string): boolean {
    const tabMapping: Record<string, { reviewStatusId: number } | undefined> = {
      Behavior: this.fidelityData?.behavior,
      Assesment: this.fidelityData?.assesment,
      Strategy: this.fidelityData?.stretagy,
      Data: this.fidelityData?.data,
    };

    const tabData = tabMapping[tabName];
    if (tabData) {
      return (
        tabData.reviewStatusId === this.reviewStatus['Changes Requested'] ||
        tabData.reviewStatusId === this.reviewStatus['Changes Received']
      );
    }

    return false;
  }

  getfidelityByBehvaiorIdandType() {
    if (this.drawerParams?.behavior?.id) {
      this.loaderService.show();
      this.fidelityService
        .getFidelityBybehvaiorIdandType(this.drawerParams.behavior?.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.loaderService.hide();
            if (response.result) {
              this.fidelityData = response.result;
              this.selectedTab = this.getVisibleTabs()[0] || '';
              this.selectedTabIndex = 0;
              this.setBehvaiorData();
              this.cd.detectChanges();
            }
          },
          error: error => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  setBehvaiorData() {
    this.behvaiorInputData = {
      selectedTab: this.selectedTab,
      selectedTabIndex: this.selectedTabIndex,
      caseBehvaiorId: this.drawerParams?.behavior?.id ?? 0,
      fidelityData: this.fidelityData,
      caseDetail: this.drawerParams?.caseDetail || null,
    };
  }

  formValueChange(event: FormEventEmit): void {
    this.fidelityPaylod = event.formValue;
    this.isFormValid = event.isFormValid;
  }

  onCancel() {
    this.drawerService.closeDrawer(
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  saveandClose() {
    this.saveFidelityCheck(true);
  }

  saveFidelityCheck(isClose = false): void {
    this.fidelityPaylod['caseBehaviourId'] =
      this.drawerParams?.behavior?.id ?? 0;
    const isEditMode =
      this.fidelityPaylod && this.fidelityPaylod?.id ? true : false;

    this.loaderService.show();
    const operation$ = isEditMode
      ? this.fidelityService.updateFidelityCheck(
          this.fidelityPaylod,
          this.fidelityPaylod.id ?? 0
        )
      : this.fidelityService.createFidelityCheck(this.fidelityPaylod);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `${this.fidelityCheckLabel} ${isEditMode ? 'updated' : 'created'} successfully`
          );
          if (isClose)
            this.drawerService.closeDrawer(
              DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
            );
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError(error: ErrorResponse): void {
    this.errorMessage = error?.message;
    this.commonService.notify('error', this.errorMessage);
  }

  handleEmailSend(event: sendEmailPayload) {
    this.emailPayload = event;
  }

  sendEmail() {
    const emailPayload = {
      to: this.emailPayload.emails ?? [],
      subject: this.emailPayload.subject,
      body: this.emailPayload.body,
      isHtml: true,
    };
    this.loaderService.show();
    const operation$ =
      this.fidelityService.sendEmailNotifications(emailPayload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `${this.fidelityCheckLabel} emails sent successfully`
          );
          this.drawerService.closeDrawer(
            DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
          );
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
